import { API_BASE_URL } from "src/config/apiBase";
import { Plus } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { decryptToken } from "../utils/getToken";

const CategorySelector = ({ categories: initialCategories, onSelect, selected = [] }) => {
  // categories now store objects { name, parent? }

  const url = API_BASE_URL;
  const token = decryptToken("accessToken") || "";

  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCategories, setSelectedCategories] = useState(
    Array.isArray(selected) ? selected : []
  );

  const [newParent, setNewParent] = useState("");

  // Load `initialCategories` only once
  useEffect(() => {
    if (Array.isArray(initialCategories) && initialCategories.length) {
      const mapped = initialCategories.map((c) => ({
        id: c.id,
        name: c.name,
        parent_id: c.parent_id || null,
        parent: c.parentDetails?.name || null,
      }));
      setCategories(mapped);
    }
  }, [initialCategories]);

  // Sync external selected → internal only if changed
  useEffect(() => {
    if (Array.isArray(selected) && selected !== selectedCategories) {
      setSelectedCategories(selected);
    }
  }, [selected]);


  // Send selection updates upward only when changed
  useEffect(() => {
    onSelect?.(selectedCategories);
  }, [selectedCategories]);

  // Filtered list memoized
  const filtered = useMemo(() => {
    return categories.filter((cat) =>
      cat?.name?.toLowerCase().includes(search.toLowerCase())
    );
  }, [categories, search]);


  // Toggle
  const toggleCategory = (cat) => {
    setSelectedCategories((prev) =>
      prev.some((c) => c.id === cat.id)
        ? prev.filter((c) => c.id !== cat.id)
        : [...prev, { id: cat.id, name: cat.name, parent_id: cat.parent_id }]
    );
  };

  const handleAddCategory = async () => {
    if (!search.trim() || categories.some((c) => c.name === search)) return;

    const payload = {
      name: search,
      parent_id: newParent || null,
      slug: search.toLowerCase().replace(/\s+/g, "-"),
    };

    try {
      const response = await fetch(`${url}/blog-category`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const res = await response.json();
      if (response.ok) {
        const newCat = {
          id: res.data.id,
          name: res.data.name,
          parent_id: res.data.parent_id || null,
          parent: res.data.parentDetails?.name || null,
        };

        setCategories(prev => [...prev, newCat]);
        setSelectedCategories(prev => [...prev, newCat]);
      }

    } catch (err) {
      console.error(err);
    }

    setSearch("");
    setNewParent("");
  };

  return (
    <>
      <h3 className="text-[16px] font-semibold text-black/80">Category</h3>

      {/* Search input */}
      <div className="pt-2 mb-3">
        <input
          type="text"
          placeholder="Search or create category"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full text-black placeholder-gray-500 border-b-2 border-gray-300 focus:border-blue-500 outline-none py-2 px-2 transition text-sm"
        />
      </div>

      {/* Categories */}
      <div className="flex flex-wrap gap-2 mb-3">
        {filtered.length > 0 ? (
          filtered.map((cat, index) => {
            const isSelected = selectedCategories.some((c) => c.id === cat.id);
            return (
              <button
                key={index}
                onClick={() => toggleCategory(cat)}
                className={`px-2 py-0.5 text-[12px] rounded-full border transition-all cursor-pointer ${isSelected
                  ? "bg-blue-500 text-white border-blue-500"
                  : "bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200"
                  }`}
              >
                {cat.name} {cat.parent ? `(${cat.parent})` : ""}
              </button>
            );
          })
        ) : search.trim() ? (
          <div className="flex flex-col items-center gap-2 w-full">
            {/* Parent selection */}
            <select
              value={newParent}
              onChange={(e) => setNewParent(e.target.value)}
              className="w-full border rounded px-2 py-1 text-sm border-dashed border-black text-black hover:bg-blue-50 transition outline-0"
            >
              <option value="">No parent</option>
              {initialCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>

            {/* Add button */}
            <button
              onClick={handleAddCategory}
              className="cursor-pointer flex items-center gap-1 px-3 py-1 text-[12px] rounded-full border border-dashed border-black text-black hover:bg-blue-50 transition"
            >
              <Plus size={18} /> Create “{search}”
            </button>
          </div>
        ) : (
          <p className="text-gray-400 text-sm">No categories found.</p>
        )}
      </div>
    </>
  );
};

export default CategorySelector;
